import { useCallback, useEffect, useState } from 'react'
import type { StartShareOptions } from '@/hooks/useCallSession'
import { Button, Modal } from '@/components/ui'
import { focusDesktopWindow } from '@/lib/desktop'

/** Keep captures in the main document so closing the meeting view cannot end them. */
export function useMeetingSharePrompt(callId: string | null, startShare: (options?: StartShareOptions) => Promise<void>, title = 'your meeting') {
  const [request, setRequest] = useState<{ callId: string; options: StartShareOptions } | null>(null)
  const [capturing, setCapturing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { setRequest(null) }, [callId])
  const requestShare = useCallback(async (options: StartShareOptions = {}) => {
    if (callId === null) return
    setError(null)
    setRequest({ callId, options })
    focusDesktopWindow()
    window.focus()
  }, [callId])
  const busy = capturing !== null && capturing === callId
  const current = request !== null && request.callId === callId ? request : null
  const dismiss = () => { if (!busy) setRequest(null) }
  const confirm = () => {
    if (current === null || busy) return
    setCapturing(current.callId)
    // Called directly by this main-window click. A popup's gesture cannot be
    // reused here, and its own capture would end as soon as it was closed.
    void startShare(current.options).then(
      () => setRequest(value => value === current ? null : value),
      () => setError('Screen sharing could not start. Try again.'),
    ).finally(() => setCapturing(value => value === current.callId ? null : value))
  }
  const prompt = (
    <Modal
      open={current !== null} title="Share a window or screen" onClose={dismiss}
      closeOnOverlayClick={false} closeDisabled={busy}
      footer={<>
        <Button variant="ghost" onClick={dismiss} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={confirm} loading={busy}>Choose what to share</Button>
      </>}
    >
      {error === null ? null : <p role="alert">{error}</p>}
      <p>Share with {title}. Choose the window or screen you want the others to see. Sharing continues when the separate meeting window is closed.</p>
      {current?.options.takeover === true ? <p>This replaces the screen that is currently being shared.</p> : null}
    </Modal>
  )
  return { requestShare, prompt }
}
